import { useAuth } from "../lib/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { BACKEND_URL } from "../lib/api";
import { Button } from "./ui/button";
import { 
  AlertTriangle, 
  CreditCard, 
  Clock, 
  XCircle,
  ArrowRight,
  RefreshCw
} from "lucide-react";

const STATUS_CONFIG = {
  cancelled: {
    icon: XCircle,
    title: "Subscription Cancelled",
    message: "Your subscription has been cancelled. To continue using Rotalie, please select a plan.",
    primaryAction: "Select a Plan",
    primaryPath: "/dashboard/billing",
    showUpdatePayment: false,
    color: "text-red-500",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/20"
  },
  expired: {
    icon: Clock,
    title: "Trial Expired",
    message: "Your free trial has ended. Subscribe now to continue managing your team's schedules.",
    primaryAction: "Choose a Plan",
    primaryPath: "/dashboard/billing",
    showUpdatePayment: false,
    color: "text-amber-500",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/20"
  },
  past_due: {
    icon: AlertTriangle,
    title: "Payment Past Due",
    message: "Your last payment was unsuccessful. Please update your payment method to restore access.",
    primaryAction: "Update Payment",
    primaryPath: "/dashboard/billing",
    showUpdatePayment: true,
    color: "text-amber-500",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/20"
  },
  payment_failed: {
    icon: CreditCard,
    title: "Payment Failed",
    message: "We couldn't process your payment. This could be due to insufficient funds, an expired card, or your bank declining the transaction.",
    primaryAction: "Update Payment Method",
    primaryPath: "/dashboard/billing",
    showUpdatePayment: true,
    color: "text-red-500",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/20"
  },
  card_expired: {
    icon: CreditCard,
    title: "Card Expired",
    message: "The card on file has expired. Please update your payment method to continue using Rotalie.",
    primaryAction: "Update Card",
    primaryPath: "/dashboard/billing",
    showUpdatePayment: true,
    color: "text-amber-500",
    bgColor: "bg-amber-500/10",
    borderColor: "border-amber-500/20"
  },
  unpaid: {
    icon: AlertTriangle,
    title: "Subscription Unpaid",
    message: "Your subscription is currently unpaid. Please resolve the payment issue to restore access.",
    primaryAction: "Resolve Payment",
    primaryPath: "/dashboard/billing",
    showUpdatePayment: true,
    color: "text-red-500",
    bgColor: "bg-red-500/10",
    borderColor: "border-red-500/20"
  }
};

// Statuses that should block app access
const BLOCKING_STATUSES = ['cancelled', 'expired', 'past_due', 'payment_failed', 'card_expired', 'unpaid'];

const SubscriptionBlocker = ({ children }) => {
  const { company, subscription, trialDaysRemaining } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Allow access to billing page even when blocked (so they can fix the issue)
  const isOnBillingPage = location.pathname.includes('/billing') || location.pathname.includes('/account');
  
  // Determine if we should block access
  const subscriptionStatus = subscription?.status || company?.subscription_status;
  const paymentStatus = subscription?.payment_status;
  
  // Check various blocking conditions
  let blockingStatus = null;
  
  if (subscriptionStatus === 'cancelled') {
    blockingStatus = 'cancelled';
  } else if (subscriptionStatus === 'expired' || (subscriptionStatus === 'trial' && trialDaysRemaining <= 0)) {
    blockingStatus = 'expired';
  } else if (subscriptionStatus === 'past_due' || paymentStatus === 'past_due') {
    blockingStatus = 'past_due';
  } else if (paymentStatus === 'failed' || subscriptionStatus === 'payment_failed') {
    blockingStatus = 'payment_failed';
  } else if (subscriptionStatus === 'unpaid') {
    blockingStatus = 'unpaid';
  }
  
  // If not blocking, render children normally
  // Also allow billing page access so users can fix payment issues
  if (!blockingStatus || !BLOCKING_STATUSES.includes(blockingStatus) || isOnBillingPage) {
    return children;
  }

  const config = STATUS_CONFIG[blockingStatus] || STATUS_CONFIG.cancelled;
  const IconComponent = config.icon;

  const handlePrimaryAction = () => {
    navigate(config.primaryPath);
  };

  const handleManageSubscription = async () => {
    // Open Stripe customer portal if they have a customer ID
    if (company?.stripe_customer_id) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/billing/portal`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ return_url: window.location.origin + '/dashboard/billing' })
        });
        const data = await response.json();
        if (data.portal_url) {
          window.location.href = data.portal_url;
        }
      } catch (error) {
        console.error('Failed to open billing portal:', error);
        navigate('/dashboard/billing');
      }
    } else {
      navigate('/dashboard/billing');
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Blurred background - show the app behind */}
      <div className="blur-sm pointer-events-none opacity-30">
        {children}
      </div>
      
      {/* Overlay */}
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`max-w-md w-full rounded-2xl border ${config.borderColor} ${config.bgColor} p-8 shadow-2xl`}>
          {/* Icon */}
          <div className={`w-16 h-16 rounded-full ${config.bgColor} flex items-center justify-center mx-auto mb-6`}>
            <IconComponent className={`w-8 h-8 ${config.color}`} />
          </div>
          
          {/* Title */}
          <h2 className={`text-2xl font-bold text-center mb-3 ${config.color}`}>
            {config.title}
          </h2>
          
          {/* Message */}
          <p className="text-center text-muted-foreground mb-8">
            {config.message}
          </p>
          
          {/* Actions */}
          <div className="space-y-3">
            <Button 
              onClick={handlePrimaryAction}
              className="w-full"
              size="lg"
              data-testid="subscription-blocker-primary-btn"
            >
              {config.primaryAction}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            {config.showUpdatePayment && company?.stripe_customer_id && (
              <Button 
                onClick={handleManageSubscription}
                variant="outline"
                className="w-full"
                size="lg"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Manage in Stripe
              </Button>
            )}
          </div>
          
          {/* Help text */}
          <p className="text-xs text-center text-muted-foreground mt-6">
            Need help? Contact us at{" "}
            <a href="mailto:support@rotalie.io" className="underline hover:text-foreground">
              support@rotalie.io
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBlocker;
